import { cutoutUrl } from '../../commerce/cutouts';
import { prefersReducedMotion } from '../../scenes/scene.transitions';
import type { ShopProduct } from '../../commerce/shopify.types';

export interface ProductStage {
  element: HTMLElement;
  /** `origine` est la vignette cliquée : le produit en sort. */
  open(product: ShopProduct, origine?: HTMLElement): void;
  close(): void;
  destroy(): void;
}

/**
 * Le produit sort de l'étagère : l'image part de la vignette cliquée et vient
 * se poser, agrandie, à gauche de l'écran, pendant que la fiche s'ouvre à
 * droite. Les autres photos du produit se choisissent en dessous.
 */
export function createProductStage(parent: HTMLElement): ProductStage {
  const element = document.createElement('div');
  element.className = 'product-stage';
  element.setAttribute('aria-hidden', 'true');
  element.hidden = true;

  const hero = document.createElement('img');
  hero.className = 'product-stage__hero';
  hero.alt = '';
  hero.decoding = 'async';

  const thumbs = document.createElement('div');
  thumbs.className = 'product-stage__thumbs';

  element.append(hero, thumbs);
  parent.append(element);

  function close(): void {
    element.hidden = true;
    element.classList.remove('is-open');
  }

  /** Animation FLIP : on part de la position réelle de la vignette. */
  function vole(origine: HTMLElement): void {
    if (prefersReducedMotion()) return;
    const depart = origine.getBoundingClientRect();
    const arrivee = hero.getBoundingClientRect();
    if (arrivee.width === 0 || arrivee.height === 0) return;

    const echelle = Math.max(0.12, depart.width / arrivee.width);
    const dx = depart.left + depart.width / 2 - (arrivee.left + arrivee.width / 2);
    const dy = depart.top + depart.height / 2 - (arrivee.top + arrivee.height / 2);

    hero.animate(
      [
        { transform: `translate(${dx}px, ${dy}px) scale(${echelle})`, opacity: 0.4 },
        { transform: 'none', opacity: 1 },
      ],
      { duration: 480, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' },
    );
  }

  function open(product: ShopProduct, origine?: HTMLElement): void {
    const images = product.images.length
      ? product.images
      : product.imageUrl
        ? [{ url: product.imageUrl, alt: product.imageAlt || product.title }]
        : [];

    const premiere = images[0];
    if (!premiere) {
      close();
      return;
    }

    // La photo principale profite du détourage ; les autres restent telles quelles.
    hero.src = cutoutUrl(product.handle, premiere.url);
    hero.alt = premiere.alt || product.title;

    thumbs.replaceChildren();
    // Une seule photo : la rangée de miniatures n'apporte rien.
    if (images.length > 1) {
      images.forEach((image, index) => {
        const bouton = document.createElement('button');
        bouton.type = 'button';
        bouton.className = 'product-stage__thumb';
        bouton.setAttribute('aria-label', `Photo ${index + 1} de ${product.title}`);
        if (index === 0) bouton.classList.add('is-active');

        const vignette = document.createElement('img');
        vignette.src = image.url;
        vignette.alt = '';
        vignette.loading = 'lazy';
        bouton.append(vignette);

        bouton.addEventListener('click', () => {
          hero.src = image.url;
          hero.alt = image.alt || product.title;
          for (const autre of thumbs.querySelectorAll('.product-stage__thumb')) {
            autre.classList.remove('is-active');
          }
          bouton.classList.add('is-active');
        });

        thumbs.append(bouton);
      });
    }

    element.hidden = false;
    requestAnimationFrame(() => {
      element.classList.add('is-open');
      if (origine) vole(origine);
    });
  }

  return {
    element,
    open,
    close,
    destroy(): void {
      element.remove();
    },
  };
}
